import { Clock, Plus, X } from "lucide-react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { SettingCard } from "@/components/settings/setting-card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { BusinessHoursTimeSelect } from "@/components/ui/compact-time-select";
import { CenteredSpinner } from "@/lib/components/CenteredSpinner";
import { createTimeSlot } from "../../../types";
import type { BusinessSettingsFormData } from "../type/business-settings";
import { SyncFromGoogleButton } from "./sync-from-google-button";

interface OpeningHoursFormProps {
  /** Loading state for initial data fetch */
  loading?: boolean;
  /** Saving state during mutations */
  saving?: boolean;
  /** Render without the SettingCard wrapper (e.g. inside a dialog). */
  bare?: boolean;
}

interface DayOfWeekTimeSlotsProps {
  dayName: string;
  dayOfWeek: number;
  disabled?: boolean;
}

function DayOfWeekTimeSlots({ dayName, dayOfWeek, disabled = false }: DayOfWeekTimeSlotsProps) {
  const { t } = useTranslation("settings");
  const form = useFormContext<BusinessSettingsFormData>();

  const {
    fields: timeSlots,
    insert: insertTimeSlot,
    remove: removeTimeSlot,
    update: updateTimeSlot,
  } = useFieldArray({
    control: form.control,
    name: `operatingHours.weeklyHours.${dayOfWeek}.timeSlots`,
  });

  // Add a new time slot
  const handleAddSlot = () => {
    const newSlot = createTimeSlot("09:00", "17:00", timeSlots.length + 1);
    insertTimeSlot(timeSlots.length, newSlot);
  };

  // Remove a time slot
  const handleRemoveSlot = (slotIndex: number) => {
    removeTimeSlot(slotIndex);
  };

  // Update a specific time slot
  const handleSlotChange = (slotIndex: number, field: "startTime" | "endTime", value: string) => {
    const currentSlot = timeSlots[slotIndex];
    const updatedSlot = { ...currentSlot, [field]: value };
    updateTimeSlot(slotIndex, updatedSlot);
  };

  // Check if start time is before end time
  const isValidTimeRange = (startTime: string, endTime: string): boolean => {
    return startTime < endTime;
  };

  // Day is closed when there are no time slots
  const isClosed = timeSlots.length === 0;

  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3 py-2 border-b border-border/40 last:border-0">
      {/* Day name - full width on mobile, fixed width on desktop */}
      <div className="sm:w-24 sm:pt-2">
        <p className="font-medium text-sm">{t(`business.daysOfWeek.${dayName.toLowerCase()}`)}</p>
      </div>

      {/* Time slots - flex wrap container */}
      <div className="flex-1 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-2">
        {isClosed ? (
          <>
            <Badge variant="secondary" className="text-xs">
              {t("business.closedDays")}
            </Badge>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleAddSlot}
              disabled={disabled}
              className="h-8 px-2 text-xs"
            >
              <Plus className="h-3 w-3 mr-1" />
              {t("business.addHours")}
            </Button>
          </>
        ) : (
          <>
            {timeSlots.map((slot, slotIndex) => (
              <div
                key={slot.id || slotIndex}
                className="flex items-center gap-2 bg-accent/50 rounded-md px-3 py-1.5 w-full sm:w-auto"
              >
                <div className="flex items-center gap-1 flex-1 sm:flex-initial">
                  <BusinessHoursTimeSelect
                    value={slot.startTime}
                    onChange={(value) => handleSlotChange(slotIndex, "startTime", value)}
                    disabled={disabled}
                    size="sm"
                    className="flex-1 sm:flex-initial"
                  />
                  <span className="text-xs text-muted-foreground mx-1">-</span>
                  <BusinessHoursTimeSelect
                    value={slot.endTime}
                    onChange={(value) => handleSlotChange(slotIndex, "endTime", value)}
                    disabled={disabled}
                    size="sm"
                    className="flex-1 sm:flex-initial"
                  />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemoveSlot(slotIndex)}
                  disabled={disabled}
                  className="h-6 w-6 p-0 flex-shrink-0"
                >
                  <X className="h-3 w-3" />
                </Button>
                {!isValidTimeRange(slot.startTime, slot.endTime) && (
                  <span className="text-xs text-destructive flex-shrink-0">
                    {t("business.invalid")}
                  </span>
                )}
              </div>
            ))}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleAddSlot}
              disabled={disabled}
              className="h-8 px-2 text-xs"
            >
              <Plus className="h-3 w-3 mr-1" />
              {t("business.addSlot")}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

export function OpeningHoursForm({
  loading = false,
  saving = false,
  bare = false,
}: OpeningHoursFormProps) {
  const { t } = useTranslation("settings");
  const form = useFormContext<BusinessSettingsFormData>();
  const { fields: weeklyHoursFields } = useFieldArray({
    control: form.control,
    name: "operatingHours.weeklyHours",
  });

  // Sort days to show Monday-Sunday for display
  const sortedDays = [...weeklyHoursFields].sort((a, b) => {
    // Convert Sunday (0) to 7 for sorting, so Monday (1) comes first
    const aSort = a.dayOfWeek === 0 ? 7 : a.dayOfWeek;
    const bSort = b.dayOfWeek === 0 ? 7 : b.dayOfWeek;
    return aSort - bSort;
  });

  const content = loading ? (
    <CenteredSpinner minHeight="min-h-[200px]" />
  ) : (
    <div className="divide-y divide-border/40">
      {sortedDays.map((day) => (
        <DayOfWeekTimeSlots
          key={day.dayOfWeek}
          dayName={day.dayName}
          dayOfWeek={day.dayOfWeek}
          disabled={saving}
        />
      ))}
    </div>
  );

  if (bare) return content;

  return (
    <SettingCard title={t("business.openingHours")} icon={<Clock className="h-5 w-5" />}>
      <div className="flex justify-end pb-2">
        <SyncFromGoogleButton />
      </div>
      <div className="divide-y divide-border/40">
        {sortedDays.map((day) => (
          <DayOfWeekTimeSlots
            key={day.dayOfWeek}
            dayName={day.dayName}
            dayOfWeek={day.dayOfWeek}
            disabled={saving}
          />
        ))}
      </div>
    </SettingCard>
  );
}
